import React from 'react';
import { observer } from 'mobx-react';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import authStore from '~/stores/authStore';
import SignUpBtn from '~/components/auth/SignUpBtn';
import {
    prices,
    WTM_PROMO_ACTIVE,
    WTM_OLD_PRICE_MULTIPLIER,
    WTM_PROMO_MIN_ACCOUNTS
} from '~/components/Admin/Calculator';

const useStyles = makeStyles(({ spacing, palette }) =>
    createStyles({
        root: {
            padding: spacing(6, 0),
            background: 'linear-gradient(135deg, rgba(37,211,102,0.06), rgba(0,136,204,0.06), rgba(115,60,228,0.06))',
            textAlign: 'center'
        },
        badge: {
            display: 'inline-block',
            background: '#fb4934',
            color: '#fff',
            borderRadius: 4,
            padding: spacing(0.5, 2),
            fontWeight: 700,
            fontSize: '0.85rem',
            letterSpacing: 1,
            textTransform: 'uppercase' as const,
            marginBottom: spacing(2)
        },
        title: {
            fontWeight: 700,
            color: palette.primary.main,
            marginBottom: spacing(1)
        },
        gradientLine: {
            height: 3,
            width: 120,
            margin: '0 auto',
            background: 'linear-gradient(90deg, #25D366, #0088cc, #733ce4)',
            borderRadius: 2,
            marginBottom: spacing(2)
        },
        subtitle: {
            color: palette.text.secondary,
            maxWidth: 600,
            margin: '0 auto',
            marginBottom: spacing(3),
            lineHeight: 1.6
        },
        priceBox: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing(3),
            marginBottom: spacing(3),
            flexWrap: 'wrap' as const
        },
        priceCard: {
            padding: spacing(2, 3),
            borderRadius: 12,
            textAlign: 'center' as const,
            minWidth: 140
        },
        oldPriceCard: {
            background: 'rgba(0,0,0,0.04)',
            border: '1px solid rgba(0,0,0,0.08)'
        },
        newPriceCard: {
            background: 'linear-gradient(135deg, rgba(37,211,102,0.12), rgba(0,136,204,0.12), rgba(115,60,228,0.12))',
            border: '2px solid #fb4934',
            boxShadow: '0 4px 20px rgba(251,73,52,0.15)'
        },
        priceLabel: {
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase' as const,
            letterSpacing: 1,
            marginBottom: spacing(0.5)
        },
        oldPrice: {
            textDecoration: 'line-through',
            color: palette.text.secondary,
            fontSize: '1.5rem',
            fontWeight: 500
        },
        discountBadge: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center'
        },
        discountCircle: {
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#fb4934',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: '1.1rem',
            boxShadow: '0 2px 12px rgba(251,73,52,0.35)'
        },
        newPrice: {
            color: '#fb4934',
            fontWeight: 800,
            fontSize: '2rem',
            lineHeight: 1.2
        },
        perMonth: {
            color: palette.text.secondary,
            fontSize: '0.85rem',
            display: 'block',
            marginTop: spacing(0.5)
        },
        // Условие акции остаётся внутри карточки цены, но подчинено ей отбивкой.
        minQty: {
            display: 'block',
            marginTop: spacing(1),
            paddingTop: spacing(1),
            borderTop: '1px solid rgba(251,73,52,0.2)',
            color: palette.text.secondary,
            fontSize: '0.75rem',
            fontWeight: 500
        },
        pills: {
            display: 'flex',
            justifyContent: 'center',
            gap: spacing(2),
            marginBottom: spacing(3),
            flexWrap: 'wrap' as const
        },
        pill: {
            padding: spacing(1, 2.5),
            borderRadius: 8,
            fontWeight: 600,
            fontSize: '0.95rem',
            color: '#fff'
        },
        disclaimer: {
            color: palette.text.secondary,
            fontSize: '0.75rem',
            marginTop: spacing(3),
            opacity: 0.7,
            maxWidth: 480,
            margin: `${spacing(3)}px auto 0`
        }
    })
);

const langLib = {
    en: {
        badge: 'LIMITED OFFER',
        title: 'All three messengers for the price of one',
        subtitle: 'The messenger landscape is changing. Stay connected on every channel \u2014 WhatsApp, Telegram, and MAX \u2014 without paying extra.',
        oldPriceLabel: 'Was from',
        newPriceLabel: 'Now from',
        perMonth: '/mo per number',
        minQty: 'on orders from {n} numbers',
        cta: 'Get started',
        disclaimer: '* This is a limited-time promotion. The offer may be withdrawn at any time without prior notice. The duration of the promotion is not predetermined.'
    },
    ru: {
        badge: 'АКЦИЯ',
        title: 'Три мессенджера по цене одного',
        subtitle: 'Пока ситуация с мессенджерами в России меняется — подключите WhatsApp, Telegram и MAX по цене одного. Ваш бизнес останется на связи с клиентами на любой платформе.',
        oldPriceLabel: 'Было от',
        newPriceLabel: 'Стало от',
        perMonth: '/мес. за номер',
        minQty: 'при заказе от {n} номеров',
        cta: 'Начать',
        disclaimer: '* Акция носит временный характер. Сроки проведения не установлены.'
    }
} as const;

const PromoBanner = () => {
    const classes = useStyles();
    const { lang } = authStore;
    const lib = langLib[lang];

    if (!WTM_PROMO_ACTIVE) return null;

    // Цена того тира, с которого начинает действовать акция, — чтобы «от 999 ₽»
    // соответствовало заявленному условию «от 100 номеров».
    const pricesLang = prices[lang];
    const promoTier = pricesLang.find(({ numbers }) => numbers === WTM_PROMO_MIN_ACCOUNTS) ?? pricesLang[pricesLang.length - 1];
    const newPrice = promoTier.priceWTM;
    const oldPrice = newPrice * WTM_OLD_PRICE_MULTIPLIER;

    return (
        <div className={classes.root}>
            <Container maxWidth="md">
                <span className={classes.badge}>{lib['badge']}</span>
                <Typography variant="h4" className={classes.title}>
                    {lib['title']}
                </Typography>
                <div className={classes.gradientLine} />
                <Typography className={classes.subtitle}>
                    {lib['subtitle']}
                </Typography>

                <Box className={classes.pills}>
                    <span className={classes.pill} style={{ background: '#25D366' }}>WhatsApp</span>
                    <span className={classes.pill} style={{ background: '#0088cc' }}>Telegram</span>
                    <span className={classes.pill} style={{ background: '#733ce4' }}>MAX (VK)</span>
                </Box>

                <Box className={classes.priceBox}>
                    <div className={`${classes.priceCard} ${classes.oldPriceCard}`}>
                        <div className={classes.priceLabel} style={{ color: '#999' }}>{lib['oldPriceLabel']}</div>
                        <span className={classes.oldPrice}>{authStore.printCost(oldPrice)}</span>
                    </div>
                    <div className={classes.discountBadge}>
                        <div className={classes.discountCircle}>-50%</div>
                    </div>
                    <div className={`${classes.priceCard} ${classes.newPriceCard}`}>
                        <div className={classes.priceLabel} style={{ color: '#fb4934' }}>{lib['newPriceLabel']}</div>
                        <span className={classes.newPrice}>{authStore.printCost(newPrice)}</span>
                        <span className={classes.perMonth}>{lib['perMonth']}</span>
                        <span className={classes.minQty}>
                            {lib['minQty'].replace('{n}', String(WTM_PROMO_MIN_ACCOUNTS))}
                        </span>
                    </div>
                </Box>

                <SignUpBtn>
                    <Button color="secondary" variant="contained" size="large">
                        {lib['cta']}
                    </Button>
                </SignUpBtn>
                <Typography className={classes.disclaimer}>
                    {lib['disclaimer']}
                </Typography>
            </Container>
        </div>
    );
};

export default observer(PromoBanner);
